import { xcss, Box } from '@atlaskit/primitives';
import usePanel from '@databases/panel/usePanel';
import usePanelState from '@databases/panel/usePanelState';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectSelectedEntryIds,
  selectSelectedEntireFieldsCount,
  selectSelectedFieldIds,
  selectSelectedValuesCount,
  type ContentSelectionMode,
  selectSelectedValues,
  selectHasEntryAfterSelection,
  selectHasEntryBeforeSelection,
  selectIsDeleteColumnsDisabled,
  selectHasFieldBeforeSelection,
  selectHasFieldAfterSelection,
} from '@databases/redux/cellSelections/selectors';
import { getService } from '@databases/service-container';
import {
  getDatabaseFieldOptional,
  getDatabaseEntryOptional,
  getDatabaseEntryRequired,
  getDatabaseValue,
} from '@databases/utils/database/utils';
import { getIsSortingActive } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import { isEqual } from 'lodash-es';
import React, { useMemo, useCallback, useEffect } from 'react';

import { ActionBarContent } from './ActionBarContent';
import { actionBarState } from './action-bar-state';
import { buildSelectEntriesActionsForLegacyActionBar } from './actions/entries/build-select-entries-actions-for-legacy-action-bar';
import { buildSelectFieldsActionsForLegacyActionBar } from './actions/fields/build-select-fields-actions-for-legacy-action-bar';
import { buildSelectValuesActionsForLegacyActionBar } from './actions/values/build-select-values-actions-for-legacy-action-bar';
import { DEPENDENCY_FIELD_TYPES } from './constants';
import { useActionBarAnalytics } from './hooks/useActionBarAnalytics';
import { useSharedActionsMethods } from './hooks/useSharedActionsMethods';
import { getTotalSelectedCount } from './utils';

const actionBarContainerStyles = xcss({
  position: 'fixed',
  width: 'max-content',
  zIndex: 'modal',
  left: '50%',
  transform: 'translate(-50%)',
  bottom: '54px',
  cursor: 'default',
  height: 'space.500',
});

export type ActionBarProps = {
  contentId: string;
  canEditContent: boolean;
  selectionMode: ContentSelectionMode;
  entryId: string;
};

export const ActionBar = ({
  contentId,
  canEditContent,
  selectionMode,
  entryId,
}: ActionBarProps) => {
  const { view, setViewChange } = useView();
  const dispatch = defaultStoreUtils.useDispatch();
  const selectionsActions = getService('SelectionsActions');
  const { onCloseActionBar } = useActionBarAnalytics();
  const { setActionBarState } = actionBarState;
  const { hidePanel, showPanel } = usePanel();
  const { isPanelVisible, getData } = usePanelState();
  const { entryId: panelEntryId } = getData('EntryViewPanel') || {};

  const {
    canMoveUp,
    canMoveDown,
    canMoveLeft,
    canMoveRight,
    fieldsWithSelectionsCount,
    initialValue,
    isDatabaseLocked,
    isDeleteColumnsDisabled,
    isDependencyFieldSelected,
    selectedEntireFieldsCount,
    selectedEntriesCount,
    selectedValues,
    selectedValuesCount,
    state,
  } = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const selectedFields = selectSelectedFieldIds(state, {
      contentId,
    });
    const selectedEntries = selectSelectedEntryIds(state, {
      contentId,
    });
    const selectedValues = selectSelectedValues(state, {
      contentId,
    });

    const firstSelectedField = getDatabaseFieldOptional(database, selectedFields[0]);
    const firstSelectedEntry = getDatabaseEntryOptional(database, selectedEntries[0]);

    const valuesOfSelectedEntries =
      firstSelectedField &&
      selectedEntries.map((entryId) => {
        const entry = getDatabaseEntryRequired(database, entryId);

        return getDatabaseValue(database, firstSelectedField, entry);
      });

    const allValuesEqual = valuesOfSelectedEntries?.every((v) =>
      isEqual(v.value, valuesOfSelectedEntries?.[0].value)
    );

    const initialValue =
      allValuesEqual && firstSelectedField && firstSelectedEntry
        ? getDatabaseValue(database, firstSelectedField, firstSelectedEntry)
        : undefined;

    const selectedEntriesCount =
      selectionMode === 'row' || selectionMode === 'cell' ? selectedEntries.length : 0;

    const selectedEntireFieldsCount =
      selectionMode === 'column'
        ? selectSelectedEntireFieldsCount(state, {
            contentId: contentId as string,
          })
        : 0;

    const selectedValuesCount =
      selectionMode === 'cell'
        ? selectSelectedValuesCount(state, {
            contentId: contentId as string,
          })
        : 0;

    const fieldsWithSelectionsCount =
      selectionMode === 'cell' ? selectedFields.length : 0;

    const canMoveUp = selectHasEntryBeforeSelection(state, {
      contentId,
    });
    const canMoveDown = selectHasEntryAfterSelection(state, {
      contentId,
    });

    const canMoveLeft = selectHasFieldBeforeSelection(state, {
      contentId,
    });
    const canMoveRight = selectHasFieldAfterSelection(state, {
      contentId,
    });

    const isDeleteColumnsDisabled = selectIsDeleteColumnsDisabled(state, {
      contentId,
    });

    const isDependencyFieldSelected = selectedFields.some((fieldId) => {
      const field = database.fieldsById[fieldId];

      return field && DEPENDENCY_FIELD_TYPES.has(field.type);
    });

    const isDatabaseLocked = database.meta.isLocked || false;

    return {
      canMoveUp,
      canMoveDown,
      canMoveLeft,
      canMoveRight,
      fieldsWithSelectionsCount,
      initialValue,
      isDatabaseLocked,
      isDeleteColumnsDisabled,
      isDependencyFieldSelected,
      selectedEntireFieldsCount,
      selectedEntriesCount,
      selectedValues,
      selectedValuesCount,
      state,
    };
  });

  const count = getTotalSelectedCount({
    selectionMode,
    selectedEntriesCount,
    selectedEntireFieldsCount,
    selectedValuesCount,
  });

  useEffect(() => {
    setActionBarState({
      entryId,
      totalSelectedCount: count,
      contentId,
    });
  }, [contentId, count, entryId, setActionBarState]);

  const { handleDeleteFields, shouldShowClearCells } = useSharedActionsMethods({
    contentId,
    state,
  });

  const handleOnCloseActionBar = useCallback(() => {
    onCloseActionBar({ contentId, count, selectionMode, entryId });
    selectionsActions.deselectAll();
  }, [contentId, count, entryId, onCloseActionBar, selectionsActions, selectionMode]);

  const actions = useMemo(() => {
    if (selectionMode === 'row') {
      return buildSelectEntriesActionsForLegacyActionBar({
        contentId,
        canEditContent,
        dispatch,
        entryId,
        view,
        hidePanel,
        showPanel,
        panelEntryId,
        selectionsActions,
        state,
      });
    }

    if (selectionMode === 'column') {
      return buildSelectFieldsActionsForLegacyActionBar({
        contentId,
        dispatch,
        view,
        setViewChange,
        state,
      });
    }

    return buildSelectValuesActionsForLegacyActionBar({
      contentId,
      dispatch,
      hidePanel,
      panelEntryId,
      selectionsActions,
      state,
    });
  }, [
    canEditContent,
    contentId,
    dispatch,
    entryId,
    hidePanel,
    panelEntryId,
    selectionsActions,
    selectionMode,
    setViewChange,
    showPanel,
    state,
    view,
  ]);

  const isEntryPanelActive = isPanelVisible('EntryViewPanel');
  const hasSortedFields = getIsSortingActive(view.settings);

  return (
    <Box
      id="database-action-bar-container"
      testId="action-bar-container"
      xcss={actionBarContainerStyles}
    >
      <ActionBarContent
        actions={actions}
        canEditContent={canEditContent}
        canMoveDown={canMoveDown}
        canMoveUp={canMoveUp}
        canMoveLeft={canMoveLeft}
        canMoveRight={canMoveRight}
        contentId={contentId}
        count={count}
        fieldsWithSelectionsCount={fieldsWithSelectionsCount}
        handleDeleteFields={handleDeleteFields}
        handleOnCloseActionBar={handleOnCloseActionBar}
        hasSortedFields={hasSortedFields}
        initialValue={initialValue}
        isDatabaseLocked={isDatabaseLocked}
        isDeleteColumnsDisabled={isDeleteColumnsDisabled}
        isDependencyFieldSelected={isDependencyFieldSelected}
        isEntryPanelActive={isEntryPanelActive}
        selectedEntriesCount={selectedEntriesCount}
        selectedValues={selectedValues}
        selectionMode={selectionMode}
        shouldShowClearCells={shouldShowClearCells}
      />
    </Box>
  );
};
